import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { PlaymodeType } from "@/enum/playmodeType";
import useDataGridApi from "@/common/hooks/useDataGridApi";
import { ExplorerItemType } from "@/enum/explorerItemType";
import useMediaState from "@/common/hooks/useMediaState.ts";
import useExplorerItems from "@/common/hooks/useExplorerItems";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys";
import usePlayingSegment from "@/common/hooks/usePlayingSegment";
import useRecordingSegment from "@/common/hooks/useRecordingSegment.ts";
import { audioAdapterAtom } from "@/state/jotai/atom/audioAdapterAtom";
import useCurrentExplorerItem from "@/common/hooks/useCurrentExplorerItem";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState";
import { fetchNextSegment, fetchPreviousSegment } from "@/services/api/track";
import useAudioPlaymode from "@/common/hooks/preferences/useAudioPlaymode.ts";
import useIsRecordingAudioTrack from "@/common/hooks/useIsRecordingAudioTrack.ts";
import { AudioPlayback, ExplorerItem, ExplorerItemId, MediaState, Segment } from "@/types/types";

export function useAudioPlayback(): AudioPlayback {
  const [mediaState] = useMediaState();
  const [playmode] = useAudioPlaymode();
  const [dataGridApi] = useDataGridApi();
  const [explorerItems] = useExplorerItems();
  const [playingSegment] = usePlayingSegment();
  const [recordingSegment] = useRecordingSegment();
  const [audioAdapter] = useAtom(audioAdapterAtom);
  const [isRecordingAudioTrack] = useIsRecordingAudioTrack();
  const currentExplorerItem = useCurrentExplorerItem();
  const [modalIsOpen] = useSearchParamState(URLSearchParamKeys.MODAL);
  const isPlaying = useMemo(() => MediaState.PLAYING === mediaState, [mediaState]);
  const [trackSearchParam, setTrackSearchParam] = useSearchParamState(URLSearchParamKeys.TRACK);
  const canPlayPreviousOrNextTrack = useMemo(() => {
    return audioAdapter
      && !modalIsOpen
      && !isRecordingAudioTrack
      && !recordingSegment
  }, [
    modalIsOpen,
    audioAdapter,
    isRecordingAudioTrack,
    recordingSegment
  ]);

  // Track Navigation
  const getAdjacentExplorerItem = useCallback((
    explorerItemId: ExplorerItemId,
    direction: 'next' | 'previous' = 'next',
  ): ExplorerItem | null => {
    // Haal de huidige index van het explorer item op
    let index = dataGridApi?.current?.getRowIndexRelativeToVisibleRows(explorerItemId) || 0;
    let nextRow: ExplorerItem | null = null;
    let nextRowId: ExplorerItemId | null = null;
    let nextExplorerItemType: string | null = null;

    do {
      // Verhoog of verlaag de index om naar het volgende/vorige item te gaan
      index += direction === 'next' ? 1 : -1;

      // Haal de volgende of vorige row op basis van de nieuwe index
      nextRowId = dataGridApi?.current?.getRowIdFromRowIndex(index) as ExplorerItemId;

      // Als er geen volgende/vorige row is, beëindig de loop
      if (!nextRowId) return null;

      // Haal het item op uit de explorerItems array
      nextRow = explorerItems[nextRowId];

      // Als er geen geldig item is, beëindig de loop
      if (!nextRow) return null;

      // Haal het type van het item op
      nextExplorerItemType = nextRow?.type;
    } while (nextExplorerItemType !== ExplorerItemType.TRACK);

    return nextRow;
  }, [
    dataGridApi,
    explorerItems
  ])

  const updateSearchParams = useCallback((explorerItem?: ExplorerItem | null) => {
    if (!explorerItem) return;

    if (trackSearchParam !== explorerItem.id) {
      setTrackSearchParam(explorerItem.id);
    }
  }, [
    trackSearchParam,
    setTrackSearchParam
  ]);

  // Playback Controls
  const play = useCallback(async (explorerItem?: ExplorerItem | null) => {
    if (!audioAdapter) return;
    updateSearchParams(explorerItem);

    return audioAdapter.play(explorerItem);
  }, [
    audioAdapter,
    updateSearchParams
  ]);

  const pause = useCallback(() => {
    if (!audioAdapter) return;

    audioAdapter.pause();
  }, [audioAdapter]);

  const seek = useCallback((time: number) => {
    if (!audioAdapter) return;

    audioAdapter.seek(time);
  }, [audioAdapter]);

  const replay = useCallback(() => {
    seek(0);
    play();
  }, [
    play,
    seek
  ])

  const stop = useCallback(() => {
    if (!audioAdapter) return;

    audioAdapter.stop();
  }, [
    audioAdapter,
  ]);

  const togglePlay = useCallback((explorerItem?: ExplorerItem | null) => {
    if (!audioAdapter) return;

    if (isPlaying) {
      pause();
    } else {
      play(explorerItem);
    }
  }, [
    audioAdapter,
    isPlaying,
    pause,
    play
  ]);

  // Track Navigation Controls
  const playNextTrack = useCallback((explorerItem: ExplorerItem) => {
    if (!canPlayPreviousOrNextTrack) {
      return;
    }

    // Handle SEGMENTS playmode
    if (playmode === PlaymodeType.SEGMENTS && explorerItem) {
      fetchNextSegment(explorerItem.id, audioAdapter!.getCurrentTime() || 0)
        .then((segment: Segment) => {
          seek(segment.startTime);
        });
      return;
    }

    // Handle LOOP playmode
    if (playmode === PlaymodeType.LOOP) {
      play(explorerItem);
      return;
    }

    // Find and play next track
    let nextExplorerItem = getAdjacentExplorerItem(explorerItem.id);
    if (!nextExplorerItem || nextExplorerItem.explorerItemParentId !== currentExplorerItem?.explorerItemParentId) {
      const firstRowId = dataGridApi?.current?.getRowIdFromRowIndex(0);
      if (firstRowId) {
        nextExplorerItem = explorerItems[firstRowId];
      }
    }

    if (nextExplorerItem) {
      play(nextExplorerItem);
      updateSearchParams(nextExplorerItem);
    }
  }, [
    canPlayPreviousOrNextTrack,
    playmode,
    getAdjacentExplorerItem,
    currentExplorerItem?.explorerItemParentId,
    audioAdapter,
    seek,
    play,
    dataGridApi,
    explorerItems,
    updateSearchParams
  ]);

  const playPreviousTrack = useCallback((explorerItem: ExplorerItem) => {
    if (!canPlayPreviousOrNextTrack) {
      return;
    }

    // Handle LOOP playmode
    if (playmode === PlaymodeType.LOOP) {
      play(explorerItem);
      return;
    }

    // Handle SEGMENTS playmode
    if (playmode === PlaymodeType.SEGMENTS && explorerItem && playingSegment) {
      if (audioAdapter!.getCurrentTime() > (playingSegment.startTime + 3)) {
        seek(playingSegment.startTime);
        return;
      }

      fetchPreviousSegment(explorerItem.id, playingSegment.startTime)
        .then((segment: Segment) => {
          seek(segment.startTime);
        });
      return;
    }

    // Handle current track restart
    if (audioAdapter!.getCurrentTime() > 3) {
      audioAdapter!.seek(0);
      return;
    }

    // Find and play previous track
    const previousExplorerItem = getAdjacentExplorerItem(explorerItem.id, 'previous');
    if (!previousExplorerItem ||
      previousExplorerItem.explorerItemParentId !== currentExplorerItem?.explorerItemParentId) {
      return;
    }

    play(previousExplorerItem);
    updateSearchParams(previousExplorerItem);
  }, [
    canPlayPreviousOrNextTrack,
    playmode,
    playingSegment,
    audioAdapter,
    getAdjacentExplorerItem,
    currentExplorerItem?.explorerItemParentId,
    play,
    updateSearchParams,
    seek
  ]);

  const setSource = useCallback((explorerItem: ExplorerItem) => {
    audioAdapter?.setSource(explorerItem)
    updateSearchParams(explorerItem);
  }, [
    audioAdapter,
    updateSearchParams
  ])

  return {
    stop,
    play,
    replay,
    seek,
    pause,
    togglePlay,
    playNextTrack,
    playPreviousTrack,
    setSource
  };
}
